// Custom auth — replaces the Supabase Auth client. Token lives in localStorage.
// API calls attach it as Authorization: Bearer. Worker verifies via JWT_SECRET.

import { useEffect, useState } from 'react';

const TOKEN_KEY = 'stax_reply_token';
const API_URL = import.meta.env.VITE_WORKER_API_URL;

export interface AuthMember {
  id: string;
  email: string;
  display_name: string;
  role: string;
  active?: boolean;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore (e.g. private mode) */
  }
}

function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Hydrates auth state from localStorage on mount, validates token by hitting
 * /api/me. If the call 401s, clears the token. Returns { user, loading }.
 */
export function useAuthSession() {
  const [user, setUser] = useState<AuthMember | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/api/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as { member: AuthMember };
          setUser(data.member);
        } else {
          clearToken();
          setUser(null);
        }
      })
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  return { user, loading };
}

export async function signIn(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  let data: { token?: string; error?: string } = {};
  try {
    data = await res.json();
  } catch {
    return { ok: false, error: `unexpected response (${res.status})` };
  }
  if (!res.ok || !data.token) {
    return { ok: false, error: data.error ?? `login failed (${res.status})` };
  }
  setToken(data.token);
  return { ok: true };
}

export function signOut(): void {
  clearToken();
  // Force a full reload so all in-memory state and TanStack cache resets.
  window.location.href = '/login';
}

/**
 * For the password-change form. Uses the current session token.
 */
export async function changePassword(
  newPassword: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const token = getToken();
  if (!token) return { ok: false, error: 'not signed in' };
  const res = await fetch(`${API_URL}/api/auth/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ new_password: newPassword }),
  });
  let data: { error?: string } = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) return { ok: false, error: data.error ?? `failed (${res.status})` };
  return { ok: true };
}
